import React from 'react';
import ContentBeforeCTAComponent from './ContentBeforeCTAComponent';
import ContentAfterCTAComponent from './ContentAfterCTAComponent';
import styles from './index.module.less';
import type { ButtonProps, DisclaimerLink, Theme } from '../../../core/types';
import { Color } from '../../../core/enums';
import type CardSchema from '../../../core/schemas/Card.schema';
import { Container, Column, Row } from '../../atoms/grid';
import { GroupedButtons } from '../../buttons-and-links/grouped-button';
import { useBreakpoint } from '../../styleless/breakpoint';
import { Sticky } from '../../styleless/sticky';
import Element from '../../../core/Element';
import {
  Desktop as CardDetailHeaderDesktop,
  Mobile as CardDetailHeaderMobile,
  Tablet as CardDetailHeaderTablet,
} from '../../archived/side-rail-header';

interface Props {
  data: {
    sideRail: CardSchema;
    ctas: ButtonProps[];
    disclaimerLinks: DisclaimerLink[];
    contentBeforeCTAComponent?: React.ReactNode;
    contentAfterCTAComponent?: React.ReactNode;
    contentAfterCardArt?: React.ReactNode;
  };
  customization?: {
    addLargeSideRailPaddingDesktop?: boolean;
    useSmallSideRailSpaceMobile?: boolean;
    showContentAfterCTAForMobile?: boolean;
    stickyTopOffset?: string | number;
    ctaTheme?: {
      itemTheme?: Theme;
    };
    theme?: {
      cardTitleTheme?: Theme;
      cardSubTitleTheme?: Theme;
      childComponentsTheme?: Theme;
      feeElementTheme?: {
        typographyTheme?: Theme;
        bodyTheme?: Theme;
      };
      contentAfterCTATheme?: Theme;
    };
  };
  children: any;
  qeId?: string;
}

const Mobile = ({
  data: {
    sideRail,
    contentBeforeCTAComponent,
    contentAfterCTAComponent,
    contentAfterCardArt,
  },
  customization = {},
  children,
  qeId,
}: Props) => {
  const childCustomization =
    (customization.theme && customization.theme.childComponentsTheme) || {};
  const afterCTATheme =
    (customization.theme && customization.theme.contentAfterCTATheme) || {};
  const showAfterCTAContent = customization.showContentAfterCTAForMobile;
  return (
    <Element qeId={qeId} theme={{ paddingLeft: 15, paddingRight: 15 }}>
      <Element
        className={
          customization.useSmallSideRailSpaceMobile
            ? styles.mobileSmallSpace
            : ''
        }
        theme={{ paddingTop: 10, paddingBottom: 10, bgColor: Color.White }}
      >
        <CardDetailHeaderMobile
          data={sideRail}
          customization={{
            $paddingTop: customization.useSmallSideRailSpaceMobile ? 20 : 30,
            $fontColor: Color.DeepBlue,
            ...customization,
          }}
          contentAfterCardArt={contentAfterCardArt}
        />
        <ContentBeforeCTAComponent content={contentBeforeCTAComponent} />
        {showAfterCTAContent && (
          <ContentAfterCTAComponent
            content={contentAfterCTAComponent}
            theme={afterCTATheme}
          />
        )}
      </Element>
      <Element
        theme={{
          paddingTop: customization.useSmallSideRailSpaceMobile ? 20 : 30,
          paddingBottom: 30,
          ...childCustomization,
        }}
      >
        {children}
      </Element>
    </Element>
  );
};

const Tablet = ({
  data: {
    sideRail,
    contentBeforeCTAComponent,
    contentAfterCTAComponent,
    contentAfterCardArt,
  },
  children,
  qeId,
  customization = {},
}: Props) => {
  const contentAfterCTA = contentAfterCTAComponent ? (
    <ContentAfterCTAComponent
      content={contentAfterCTAComponent}
      theme={customization.theme?.contentAfterCTATheme ?? {}}
    />
  ) : null;
  return (
    <Element qeId={qeId} theme={{ paddingLeft: 15, paddingRight: 15 }}>
      <Element
        theme={{ paddingTop: 10, paddingBottom: 10, bgColor: Color.White }}
      >
        <CardDetailHeaderTablet
          data={sideRail}
          customization={customization}
          contentAfterCTA={contentAfterCTA}
          contentAfterCardArt={contentAfterCardArt}
        />
        <ContentBeforeCTAComponent content={contentBeforeCTAComponent} />
      </Element>
      <Element theme={{ paddingTop: 30, paddingBottom: 30 }}>
        {children}
      </Element>
    </Element>
  );
};

const Desktop = ({
  data: {
    ctas,
    disclaimerLinks,
    sideRail,
    contentBeforeCTAComponent,
    contentAfterCTAComponent,
    contentAfterCardArt,
  },
  customization = {},
  children,
  qeId,
}: Props) => {
  const contentAfterCTA = contentAfterCTAComponent ? (
    <ContentAfterCTAComponent
      content={contentAfterCTAComponent}
      theme={customization.theme?.contentAfterCTATheme ?? {}}
    />
  ) : null;
  return (
    <Sticky.Container>
      <Element qeId={qeId} className={styles.root}>
        <Element
          className={styles.halfBackgroundLeft}
          theme={{ bgColor: Color.Gray01 }}
        />
        <Container>
          <div className={styles.root}>
            <Row theme={{ bgColor: Color.White }}>
              <Column $md="3" theme={{ bgColor: Color.Gray01 }}>
                <Sticky.Item $stickyTop={customization.stickyTopOffset}>
                  <Element
                    className={
                      customization.addLargeSideRailPaddingDesktop
                        ? styles.desktopExtraSpace
                        : ''
                    }
                    theme={{ paddingTop: 30 }}
                  >
                    <CardDetailHeaderDesktop
                      data={sideRail}
                      customization={customization}
                      contentAfterCardArt={contentAfterCardArt}
                    />
                    <ContentBeforeCTAComponent
                      content={contentBeforeCTAComponent}
                    />
                    <Element theme={{ paddingTop: 20 }}>
                      <GroupedButtons
                        data={{ ctas, disclaimerLinks, contentAfterCTA }}
                        customization={customization.ctaTheme}
                      />
                    </Element>
                  </Element>
                </Sticky.Item>
              </Column>
              <Column $md="1">
                <Element
                  className={
                    styles.addFakeExtraLineBecauseDesignDoesNotFollowTheGrid
                  }
                  theme={{ bgColor: Color.Gray01 }}
                />
              </Column>
              <Column $md="8" theme={{ bgColor: Color.White }}>
                <Element theme={{ paddingTop: 30, paddingBottom: 30 }}>
                  {children}
                </Element>
              </Column>
            </Row>
          </div>
        </Container>
      </Element>
    </Sticky.Container>
  );
};

const defaultCustomization = {
  addLargeSideRailPaddingDesktop: false,
  useSmallSideRailSpaceMobile: false,
  showContentAfterCTAForMobile: false,
  theme: {
    cardTitleTheme: {},
    cardSubTitleTheme: {},
    childComponentsTheme: {},
    feeElementTheme: {
      typographyTheme: {},
      bodyTheme: {},
    },
    contentAfterCTATheme: {},
  },
};

export const CardDetailHeader = ({
  data,
  customization = defaultCustomization,
  children,
  qeId = 'CardDetailHeader',
}: Props): React.ReactElement => {
  const { md, lg } = useBreakpoint();
  return (
    <Sticky.Container>
      {lg ? (
        <Desktop data={data} customization={customization} qeId={qeId}>
          {children}
        </Desktop>
      ) : md ? (
        <Tablet data={data} customization={customization} qeId={qeId}>
          {children}
        </Tablet>
      ) : (
        <Mobile customization={customization} data={data} qeId={qeId}>
          {children}
        </Mobile>
      )}
    </Sticky.Container>
  );
};
